import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Badge from "shared/Badge/Badge";
import NcImage from "shared/NcImage/NcImage";
import Loading from "components/Loading/Loading";
import Seo from "components/Seo/Seo";
import moment from "moment";
import { findAllPosts } from "services/postsService";
import { BlogPost } from "services/models";

const findPostBySlugAndExclude = (
  slug: string,
  posts: BlogPost[]
): { foundPost: BlogPost | null; updatedPosts: BlogPost[] } => {
  const index = posts.findIndex((post) => post.slug === slug);
  if (index !== -1) {
    const [foundPost] = posts.splice(index, 1);
    return { foundPost, updatedPosts: posts };
  }
  return { foundPost: null, updatedPosts: posts };
};

const POSTS_PER_PAGE = 4;

const BlogSingle = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState<BlogPost | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    if (slug) {
      findAllPosts().then((posts) => {
        const { foundPost, updatedPosts } = findPostBySlugAndExclude(
          slug,
          posts
        );
        setPost(foundPost);
        setPosts(updatedPosts);
        setCurrentPage(1);
        setLoading(false);
      });
    }
  }, [slug]);

  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const currentPosts = posts.slice(startIndex, startIndex + POSTS_PER_PAGE);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderPostRelated = (post: BlogPost) => (
    <Link
      to={`/blog/${post.slug}`}
      key={post._id}
      className="relative aspect-w-3 aspect-h-4 rounded-3xl overflow-hidden group"
    >
      <NcImage
        className="object-cover w-full h-full transform group-hover:scale-105 transition-transform duration-300"
        src={post.image}
      />
      <div className="flex flex-col justify-end items-start text-xs text-neutral-300 space-y-2.5 p-4 absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <Badge name={post.category || "Traveler"} />
        <h2 className="block text-lg font-semibold text-white ">
          <span className="line-clamp-2">{post.title}</span>
        </h2>
        <div className="flex text-neutral-200 text-sm">
          <span className="font-medium truncate">{post.author}</span>
          <span className="mx-1.5 font-medium">·</span>
          <span className="font-normal truncate">
            {moment(post?.createdAt).format("DD/MM/YYYY")}
          </span>
        </div>
      </div>
    </Link>
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center items-center mt-10 space-x-2">
        {/* Prev */}
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 rounded-full border border-neutral-300 text-[#676767] font-medium
          ${
            currentPage === 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:text-[#EF8733] hover:border-[#EF8733]"
          }`}
        >
          Prev
        </button>

        {/* Page numbers */}
        {[...Array(totalPages)].map((_, i) => {
          const page = i + 1;
          const isActive = currentPage === page;
          return (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-4 py-2 rounded-full border font-medium transition-colors ${
                isActive
                  ? "border-2 border-[#EF8733] text-white bg-[#EF8733]  font-bold"
                  : "border-neutral-300 text-[#676767] hover:border-[#EF8733] hover:text-[#EF8733]"
              }`}
            >
              {page}
            </button>
          );
        })}

        {/* Next */}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 rounded-full border border-neutral-300 text-[#676767] font-medium
          ${
            currentPage === totalPages
              ? "opacity-50 cursor-not-allowed"
              : "hover:text-[#EF8733] hover:border-[#EF8733]"
          }`}
        >
          Next
        </button>
      </div>
    );
  };

  if (loading) return <Loading />;
  if (!post) return <>Not Found</>;

  return (
    <div className="nc-PageSingle pt-8 lg:pt-16">
      <Seo
        title={post.title}
        description={post.descr}
        name={post.title}
        type="post"
      />

      <header className="container rounded-xl">
        <div className="max-w-screen-md mx-auto space-y-5">
          <Badge href="#" color="gray" name={post?.category || "Traveler"} />
          <h1 className="text-neutral-900 font-semibold text-3xl md:text-4xl lg:text-5xl dark:text-neutral-100">
            {post?.title || ""}
          </h1>
          <p className="text-neutral-500 dark:text-neutral-400">
            {post?.descr}
          </p>
        </div>
      </header>

      <NcImage
        className="w-full rounded-xl"
        containerClassName="container my-10 sm:my-12"
        src={post.image}
      />

      <div className="container space-y-10">
        <div
          id="single-entry-content"
          className="prose dark:prose-invert max-w-screen-md mx-auto"
          dangerouslySetInnerHTML={{ __html: post?.content || "" }}
        />
      </div>

      <div className="relative bg-neutral-100 dark:bg-neutral-800 py-16 lg:py-28 mt-16 lg:mt-24">
        <div className="container">
          <h2 className="text-3xl font-semibold">Related posts</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {currentPosts.length ? (
              currentPosts.map(renderPostRelated)
            ) : (
              <p>No related posts found</p>
            )}
          </div>
          {renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default BlogSingle;
